import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AnalyticsService } from '../../../shared/services/analytics.service';
import { FeatureConfigService } from '../../../shared/services/feature-config.service';
import { FeatureTypes } from '../../../shared/interfaces';
import { DURATION_OPTIONS, ALLOWED_FORMATS, DURATION_MATCH_STRINGS } from '../constants/asset-list-actions.constants';
import { AssetListProjectOption } from '../interfaces/asset-list-actions.interfaces';
import { Asset, FeatureConfigValues } from '../../../shared/components/asset-list/models/asset.model';
import { GeneralUtilsService } from '../../../shared/services/general-utils.service';

@Injectable({
  providedIn: 'root',
})
export class AssetListFilterOptionsService {
  constructor(
    private readonly analyticsService: AnalyticsService,
    private readonly featureConfigService: FeatureConfigService,
    private readonly generalUtilsService: GeneralUtilsService
  ) {}

  public updateProjectOptions(
    campaignId: string,
    assets: any[],
    unSubscribeOnDestroy: any
  ): Observable<AssetListProjectOption[]> {
    return new Observable((observer: any) => {
      this.analyticsService
        .getProjectsByCampaignId(campaignId)
        .pipe(takeUntil(unSubscribeOnDestroy))
        .subscribe({
          next: (res: any) => {
            const assetGroupedByProject: { [key: string]: any[] } = res;
            const projectOptions: AssetListProjectOption[] = this.generalUtilsService.compact(
              res.map((item: any) => {
                const assetList: string[] = item.assetList.map((asset: Asset) => asset.assetListId);
                if (assetList.length === 0 || assetGroupedByProject[item.id]?.length === 0) {
                  return null;
                }

                return {
                  id: item.id,
                  name: item.name,
                  assetList: item.assetList.map((asset: Asset) => asset.assetListId),
                };
              })
            );
            observer.next(projectOptions);
          },
          error: (err: Error) => observer.error(err),
        });
    });
  }

  public updateFocusOptions(assets: Asset[]): string[] {
    const focusValues: Set<string> = new Set<string>();
    assets.forEach((asset: Asset) => {
      if (asset.focus) {
        focusValues.add(asset.focus);
      }
    });
    return Array.from(focusValues).sort((a: string, b: string) => a.localeCompare(b));
  }

  public updateTypeOptions(assets: Asset[]): string[] {
    const typeValues: Set<string> = new Set<string>();
    assets.forEach((asset: Asset) => {
      if (asset.type) {
        typeValues.add(asset.type);
      }
    });
    return Array.from(typeValues).sort((a: string, b: string) => a.localeCompare(b));
  }

  public updateBetsOptions(assets: Asset[]): string[] {
    const betsValues: Set<string> = new Set<string>();
    assets.forEach((asset: Asset) => {
      if (asset.bets && Array.isArray(asset.bets)) {
        asset.bets.forEach((bet: string) => {
          if (bet) {
            betsValues.add(bet);
          }
        });
      }
    });
    return Array.from(betsValues).sort((a: string, b: string) => a.localeCompare(b));
  }

  public updateDurationOptions(assets: Asset[]): string[] {
    const filteredDurationOptions: Set<string> = new Set<string>();

    assets.forEach((asset: Asset) => {
      if (asset.length) {
        const lengthValue: string = String(asset.length).toLowerCase();

        if (lengthValue.includes(DURATION_MATCH_STRINGS.SHORT)) {
          filteredDurationOptions.add(DURATION_OPTIONS[0]);
        }
        if (lengthValue.includes(DURATION_MATCH_STRINGS.MEDIUM)) {
          filteredDurationOptions.add(DURATION_OPTIONS[1]);
        }
        if (lengthValue.includes(DURATION_MATCH_STRINGS.LONG)) {
          filteredDurationOptions.add(DURATION_OPTIONS[2]);
        }
      }
    });

    return DURATION_OPTIONS.filter((option: string) => filteredDurationOptions.has(option));
  }

  public updateFormatOptions(assets: Asset[]): string[] {
    const formats: string[] = assets
      .filter((asset: Asset) => Array.isArray(asset.format))
      .flatMap((asset: Asset) => asset.format)
      .filter((format: string) => ALLOWED_FORMATS.includes(format));
    return [...new Set(formats)];
  }

  public updateStatusOptions(
    assets: Asset[],
    affectedAssets: any,
    statusOptions: { value: string; label: string; color: string }[]
  ): { value: string; label: string; color: string }[] {
    let filterStatusOptions: { value: string; label: string; color: string }[] = [];

    if (affectedAssets) {
      const statuses: string[] = Array.isArray(affectedAssets)
        ? affectedAssets.map((asset: Asset) => asset.status)
        : Object.values(affectedAssets).map((asset: Asset) => asset.status);

      const existingStatuses: Set<string> = new Set(statuses);
      filterStatusOptions = statusOptions.filter((option: { value: string; label: string; color: string }) =>
        existingStatuses.has(option.value)
      );
    }

    if (
      !filterStatusOptions.some((opt: { value: string; label: string; color: string }) => opt.value === 'NOT_AFFECTED')
    ) {
      for (const asset of assets) {
        if (!affectedAssets?.[asset.id]) {
          const notAffected: { value: string; label: string; color: string } | undefined = statusOptions.find(
            (opt: { value: string; label: string; color: string }) => opt.value === 'NOT_AFFECTED'
          );
          if (notAffected) {
            filterStatusOptions.push(notAffected);
          }
          break;
        }
      }
    }

    return filterStatusOptions;
  }

  public getFeatureFlags(
    unSubscribeOnDestroy: any
  ): Observable<{ hasAgencies: boolean; hasFocuses: boolean; hasTypes: boolean }> {
    return new Observable((observer: any) => {
      this.featureConfigService
        .getFeatureConfigs([FeatureTypes.FEA_CAMPAIGN_ASSETLIST])
        .pipe(takeUntil(unSubscribeOnDestroy))
        .subscribe({
          next: (res: any) => {
            const features: FeatureConfigValues = res[FeatureTypes.FEA_CAMPAIGN_ASSETLIST];
            const hasAgencies: boolean = Boolean(features?.agency?.length);
            const hasFocuses: boolean = Boolean(features?.focus?.length);
            const hasTypes: boolean = Boolean(features?.type?.length);
            observer.next({ hasAgencies, hasFocuses, hasTypes });
          },
          error: (err: Error) => observer.error(err),
        });
    });
  }
}
